import React from 'react';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import Search from 'lucide-react/dist/esm/icons/search.js';
import Award from 'lucide-react/dist/esm/icons/award.js';
import Spade from 'lucide-react/dist/esm/icons/spade.js';
import Star from 'lucide-react/dist/esm/icons/star.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import RotateCcw from 'lucide-react/dist/esm/icons/rotate-ccw.js';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import { useContent } from '../../../contexts/ContentContext';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { WinnersCopyTicket, WinnersCopyNonWinner } from '../../../content/keys';
import { useWinnersPage } from './WinnersPageContext';
import { getWinnerAmountText, getWinnerPrizeDescription, getWinnerPrizeLabel, getWinnerTitle, isRolloverWinner } from '../../../utils/winnerDisplay';
import { formatCurrencyValue } from '../../../utils/formatCurrency';

export default function WinnersGridSection() {
  const { c } = useContent();
  const { t } = useTranslations();
  const { language } = useLanguage();
  const { editMode, handleTranslationUpdate } = useEditMode();
  const { winners, filteredWinners, viewMode, loading } = useWinnersPage();

  if (loading) {
    return (
      <div className="rounded-xl p-6 text-gray-900 dark:text-gray-300">
        {c('WinnersLoading', t('WinnersLoading') || 'Loading winners...')}
      </div>
    );
  }

  // No winners exist at all — warm, encouraging empty state
  if (winners.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="relative mx-auto mb-6 w-28 h-28">
          <div className="absolute inset-0 rounded-full opacity-20 animate-pulse" style={{ background: 'var(--charity-primary, #3b82f6)' }} />
          <div
            className="relative w-28 h-28 rounded-full flex items-center justify-center shadow-xl"
            style={{ background: `linear-gradient(135deg, var(--charity-primary, #3b82f6), var(--charity-accent, #f59e0b))` }}
          >
            <Trophy className="w-14 h-14 text-white" />
          </div>
          <Sparkles
            className="absolute -top-2 -right-2 w-8 h-8 animate-pulse"
            style={{ color: 'var(--charity-accent, #f59e0b)' }}
          />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
          {c('WinnersEmptyTitle', t('WinnersEmptyTitle') || 'No Winners Yet')}
        </h3>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-2">
          {c('WinnersEmptyMessage', t('WinnersEmptyMessage') || 'The next big winner could be you! Get your tickets and be the first to celebrate.')}
        </p>
        <p className="text-sm font-medium" style={{ color: 'var(--charity-primary, #3b82f6)' }}>
          {c('WinnersEmptyCTA', t('WinnersEmptyCTA') || 'Good luck! 🍀')}
        </p>
      </div>
    );
  }

  // Winners exist but filters returned nothing
  if (filteredWinners.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="bg-gray-100 dark:bg-gray-800 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
          <Search className="w-12 h-12 text-gray-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          <InlineTranslationEdit
            translationKey={WinnersCopyNonWinner}
            value={c(WinnersCopyNonWinner, t('WinnersCopyNonWinner') || 'No winners found')}
            fallback="No winners found"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
            className="text-xl font-bold"
          />
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          <InlineTranslationEdit
            translationKey="WinnersNoResultsHint"
            value={c('WinnersNoResultsHint', t('WinnersNoResultsHint') || 'Try adjusting your search or filter criteria')}
            fallback="Try adjusting your search or filter criteria"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
            className="text-base"
          />
        </p>
      </div>
    );
  }

  return viewMode === 'grid' ? <GridView winners={filteredWinners} t={t} /> : <ListView winners={filteredWinners} t={t} />;
}

// Grid view component
function GridView({ winners, t }) {
  const { c } = useContent();
  const { language } = useLanguage();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {winners.map((winner, index) => {
        const isRollover = isRolloverWinner(winner);
        const prizeDescription = getWinnerPrizeDescription(winner, language);
        const isPrize = winner.gameType === 'prizeDraw' || winner.isPrize === true || !!prizeDescription;
        const prizeBoxClass = isRollover
          ? 'from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 border-amber-100 dark:border-amber-800'
          : isPrize
            ? 'from-purple-50 to-fuchsia-50 dark:from-purple-900/30 dark:to-fuchsia-900/30 border-purple-100 dark:border-purple-800'
            : 'from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-green-100 dark:border-green-800';
        const prizeTextClass = isRollover
          ? 'text-amber-600 dark:text-amber-400'
          : isPrize
            ? 'text-purple-600 dark:text-purple-400'
            : 'text-green-600 dark:text-green-400';
        const prizeLabelClass = isRollover
          ? 'text-amber-700 dark:text-amber-400'
          : isPrize
            ? 'text-purple-700 dark:text-purple-400'
            : 'text-green-700 dark:text-green-400';
        const prizeLabel = isRollover
          ? t('winners.winnerPrizeLabel.rolledOver', 'Rolled Over')
          : getWinnerPrizeLabel(winner, t);
        const PrizeIcon = isRollover ? RotateCcw : isPrize ? Gift : Trophy;
        return (
        <div
          key={winner.id}
          className="flex flex-col bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
          style={{
            animation: `fade-in-up 0.6s ease-out ${index * 0.1}s both`,
          }}
        >
          <div className={`h-2 bg-gradient-to-r ${winner.color}`}></div>

          <div className="flex flex-1 flex-col p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div className={`absolute inset-0 bg-gradient-to-br ${winner.color} rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity`}></div>
                <div className={`relative w-20 h-20 bg-gradient-to-br ${winner.color} rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg group-hover:scale-110 transition-transform`}>
                  {winner.avatar}
                </div>
              </div>
            </div>

            <div className="text-center mb-4">
              <h3 className="min-h-14 flex items-center justify-center text-xl font-bold text-gray-900 dark:text-white mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                {winner.name}
              </h3>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1">
                {getWinnerTitle(winner, t)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                {winner.gameType === 'squareGame' ? `Square #${winner.ticketNumber}` : `${c(WinnersCopyTicket, 'Ticket')} ${winner.ticketNumber}`}
              </p>
            </div>

            <div className={`bg-gradient-to-br ${prizeBoxClass} rounded-lg p-4 border mb-4 relative overflow-hidden`}>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-30 transition-opacity animate-shimmer"></div>
              <div className="flex items-center justify-center gap-2 mb-1 relative z-10">
                <PrizeIcon className={`w-4 h-4 ${prizeTextClass}`} />
                <p className={`text-xs ${prizeLabelClass} font-medium uppercase`}>
                  {prizeLabel}
                </p>
              </div>
              {isPrize || winner.prize ? (
                <p className={`text-lg sm:text-xl font-bold leading-tight ${prizeTextClass} text-center relative z-10`}>
                  {prizeDescription || winner.prize || t('winners.winnerPrizeLabel.prizeWon', 'Nice Prize')}
                </p>
              ) : (
                <>
                  <p className={`text-3xl font-bold ${prizeTextClass} text-center relative z-10`}>
                    {getWinnerAmountText(winner, { language })}
                  </p>
                  {winner.secondaryPrize > 0 && Number(winner.amount) !== Number(winner.secondaryPrize) && (
                    <p className={`text-xs text-center ${prizeTextClass} relative z-10 mt-1`}>
                      + {formatCurrencyValue(winner.secondaryPrize, { language })} {t('winners.winnerPrizeLabel.secondary', 'secondary')}
                    </p>
                  )}
                </>
              )}
              {isRollover && (
                <p className="text-[11px] text-center text-amber-600 dark:text-amber-400 relative z-10 mt-1">
                  {t('winners.winnerTitle.rolloverNote', 'No winner this draw — jackpot rolled over')}
                </p>
              )}
            </div>

            {winner.gameType === 'chaseTheAce' && winner.cardDrawn && (
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700/30 dark:to-gray-800/30 rounded-lg p-3 border border-gray-200 dark:border-gray-600 mb-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Spade className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="currentColor" />
                  <p className="text-xs text-gray-700 dark:text-gray-300 font-medium uppercase">{t('winners.winnerMeta.card', 'Card')}</p>
                </div>
                <p className={`text-lg font-bold text-center ${
                  ['♥', '♦'].some(s => winner.cardDrawn.includes(s))
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {winner.cardDrawn}
                </p>
                {winner.cardsRemaining > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                    {winner.cardsRemaining} {t('winners.cardsRemaining', 'cards remaining')}
                  </p>
                )}
              </div>
            )}

            {winner.gameType === 'squareGame' && winner.squareNumber && (
              <div className="bg-gradient-to-br from-violet-50 to-blue-50 dark:from-violet-900/30 dark:to-blue-900/30 rounded-lg p-3 border border-violet-200 dark:border-violet-700 mb-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                  <p className="text-xs text-violet-700 dark:text-violet-400 font-medium uppercase">{t('winners.winningSquare', 'Winning Square')}</p>
                </div>
                <p className="text-2xl font-bold text-violet-600 dark:text-violet-400 text-center">
                  #{winner.squareNumber}
                </p>
                {winner.gridSize && (
                  <p className="text-xs text-violet-500 dark:text-violet-400 text-center mt-1">
                    {winner.gridSize} {t('winners.squareGrid', 'square grid')}
                  </p>
                )}
              </div>
            )}

            {winner.gameType === 'superballBingo' && winner.ballSelected && (
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 rounded-lg p-3 border border-amber-200 dark:border-amber-700 mb-4">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Star className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="currentColor" />
                  <p className="text-xs text-amber-700 dark:text-amber-400 font-medium uppercase">
                    {winner.isJackpot ? t('winners.superballFound', 'Superball!') : t('winners.winnerMeta.ball', 'Ball')}
                  </p>
                </div>
                <div className="flex justify-center gap-2 items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg ${
                    winner.isJackpot 
                      ? 'bg-gradient-to-br from-amber-400 to-yellow-500 ring-2 ring-amber-300 animate-pulse' 
                      : 'bg-gradient-to-br from-blue-500 to-blue-600'
                  }`}>
                    {winner.ballSelected}
                  </div>
                  {winner.isJackpot && (
                    <span className="text-amber-500 text-lg">★</span>
                  )}
                </div>
                <p className="text-xs text-amber-600 dark:text-amber-400 text-center mt-2">
                  {t('winners.winnerMeta.session', 'Session')} {winner.sessionNumber || winner.draw?.match(/Session (\d+)/)?.[1] || '?'}
                </p>
              </div>
            )}

            <div className="mt-auto space-y-2 text-sm">
              <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>{winner.date}</span>
              </div>
              <p className="text-center text-gray-500 dark:text-gray-500 font-medium">
                {winner.draw}
              </p>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}

// List view component
function ListView({ winners, t }) {
  const { c } = useContent();
  const { language } = useLanguage();

  return (
    <div className="space-y-4">
      {winners.map((winner, index) => {
        const isRollover = isRolloverWinner(winner);
        const prizeDescription = getWinnerPrizeDescription(winner, language);
        const isPrize = winner.gameType === 'prizeDraw' || winner.isPrize === true || !!prizeDescription;
        const prizeBoxClass = isRollover
          ? 'from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 border-amber-100 dark:border-amber-800'
          : isPrize
            ? 'from-purple-50 to-fuchsia-50 dark:from-purple-900/30 dark:to-fuchsia-900/30 border-purple-100 dark:border-purple-800'
            : 'from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-green-100 dark:border-green-800';
        const prizeTextClass = isRollover
          ? 'text-amber-600 dark:text-amber-400'
          : isPrize
            ? 'text-purple-600 dark:text-purple-400'
            : 'text-green-600 dark:text-green-400';
        const prizeLabelClass = isRollover
          ? 'text-amber-700 dark:text-amber-400'
          : isPrize
            ? 'text-purple-700 dark:text-purple-400'
            : 'text-green-700 dark:text-green-400';
        const prizeLabel = isRollover
          ? t('winners.winnerPrizeLabel.rolledOver', 'Rolled Over')
          : getWinnerPrizeLabel(winner, t);
        const PrizeIcon = isRollover ? RotateCcw : isPrize ? Gift : Trophy;
        return (
        <div
          key={winner.id}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all hover:-translate-y-1 group"
          style={{
            animation: `fade-in-up 0.4s ease-out ${index * 0.05}s both`,
          }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="relative flex-shrink-0">
              <div className={`absolute inset-0 bg-gradient-to-br ${winner.color} rounded-full blur-md opacity-50 group-hover:opacity-75 transition-opacity`}></div>
              <div className={`relative w-16 h-16 bg-gradient-to-br ${winner.color} rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg group-hover:scale-110 transition-transform`}>
                {winner.avatar}
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {winner.name}
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                  {winner.gameType === 'squareGame' ? `Square #${winner.ticketNumber}` : winner.ticketNumber}
                </span>
                <span className="rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                  {getWinnerTitle(winner, t)}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{winner.date}</span>
                </div>
                <span className="text-gray-400 hidden sm:inline">-</span>
                <span>{winner.draw}</span>
              </div>
            </div>

            <div className="flex-shrink-0 flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
              {winner.gameType === 'chaseTheAce' && winner.cardDrawn && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700/30 dark:to-gray-800/30 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center gap-2 mb-1">
                    <Spade className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="currentColor" />
                    <p className="text-xs text-gray-700 dark:text-gray-300 font-medium uppercase">{t('winners.winnerMeta.card', 'Card')}</p>
                  </div>
                  <p className={`text-lg font-bold text-center ${
                    ['♥', '♦'].some(s => winner.cardDrawn.includes(s))
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {winner.cardDrawn}
                  </p>
                </div>
              )}

              {winner.gameType === 'squareGame' && winner.squareNumber && (
                <div className="bg-gradient-to-br from-violet-50 to-blue-50 dark:from-violet-900/30 dark:to-blue-900/30 rounded-lg px-4 py-3 border border-violet-200 dark:border-violet-700">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                    <p className="text-xs text-violet-700 dark:text-violet-400 font-medium uppercase">{t('winners.winnerMeta.square', 'Square')}</p>
                  </div>
                  <p className="text-xl font-bold text-violet-600 dark:text-violet-400">
                    #{winner.squareNumber}
                  </p>
                </div>
              )}

              {winner.gameType === 'superballBingo' && winner.ballSelected && (
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 rounded-lg px-4 py-3 border border-amber-200 dark:border-amber-700">
                  <div className="flex items-center gap-2 mb-1">
                    <Star className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="currentColor" />
                    <p className="text-xs text-amber-700 dark:text-amber-400 font-medium uppercase">
                      {winner.isJackpot ? t('winners.superballFound', 'Superball!') : t('winners.winnerMeta.ball', 'Ball')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs shadow ${
                      winner.isJackpot 
                        ? 'bg-gradient-to-br from-amber-400 to-yellow-500 ring-2 ring-amber-300' 
                        : 'bg-gradient-to-br from-blue-500 to-blue-600'
                    }`}>
                      {winner.ballSelected}
                    </div>
                    {winner.isJackpot && <span className="text-amber-500">★</span>}
                  </div>
                </div>
              )}

              <div className={`bg-gradient-to-br ${prizeBoxClass} rounded-lg px-6 py-3 border`}>
                <div className="flex items-center gap-2 mb-1">
                  <PrizeIcon className={`w-4 h-4 ${prizeTextClass}`} />
                  <p className={`text-xs ${prizeLabelClass} font-medium uppercase`}>
                    {prizeLabel}
                  </p>
                </div>
                {isPrize || winner.prize ? (
                  <p className={`text-base sm:text-lg font-bold leading-tight ${prizeTextClass}`}>
                    {prizeDescription || winner.prize || t('winners.winnerPrizeLabel.prizeWon', 'Nice Prize')}
                  </p>
                ) : (
                  <>
                    <p className={`text-2xl font-bold ${prizeTextClass}`}>
                      {getWinnerAmountText(winner, { language })}
                    </p>
                    {winner.secondaryPrize > 0 && Number(winner.amount) !== Number(winner.secondaryPrize) && (
                      <p className={`text-xs ${prizeTextClass} mt-1`}>
                        + {formatCurrencyValue(winner.secondaryPrize, { language })}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}
